/**
 * Per-piece QR labels, 3 in x 1 in (spec §7.2). CR-2026-PIECE-LABELS (2026-10-14).
 * Server: POST/GET /api/piece-trace/items/:itemId/labels, POST /api/labels/:id/reprint.
 *
 * One label per physical piece ("03 of 24"). The QR is a link to the registered label, never a raw id. A label is
 * registered before it is printed (TR-001 §2). A voided label is never replaced silently: it needs a reprint with a
 * reason, which keeps the same piece identity (spec §7.3).
 */
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { QRCodeSVG } from "qrcode.react";
import {
  Alert, Box, Button, Container, MenuItem, Paper, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import ApiCall from "../../utils/apicall";
import { hasPermission } from "../../utils/hasPermission";

import SmartTable from "../../components/SmartTable/SmartTable";
const msgOf = (e, f) => e?.response?.data?.message || e?.message || f;

/** Labels that can be printed now (registered, not voided). Exported for tests. */
export function printable(rows) {
  return (rows || []).filter((r) => r.labelUuid && r.qr);
}

export default function PieceLabels() {
  const [params, setParams] = useSearchParams();
  const { loginUser } = useSelector((s) => s.userSlice);
  const perms = loginUser?.decodedToken?.permissions || [];
  const canReprint = hasPermission(perms, "add_heat");
  const [jobs, setJobs] = useState([]);
  const [marks, setMarks] = useState([]);
  const jobId = params.get("jobId") || "";
  const itemId = params.get("itemId") || "";
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    ApiCall.get("/job").then((r) => setJobs(r.data?.data?.jobs || r.data?.data || [])).catch(() => setJobs([]));
  }, []);
  useEffect(() => {
    setMarks([]);
    if (!jobId) return;
    ApiCall.get(`/fabricated-items/job/${jobId}`).then((r) => setMarks(Array.isArray(r.data?.data) ? r.data.data : [])).catch(() => setMarks([]));
  }, [jobId]);
  useEffect(() => {
    setRows([]); setMsg(null);
    if (!itemId) return;
    ApiCall.get(`/piece-trace/items/${itemId}/labels`).then((r) => setRows(r.data.data || [])).catch((e) => setMsg({ severity: "error", text: msgOf(e, "Could not load.") }));
  }, [itemId]);

  const register = async () => {
    setBusy(true); setMsg(null);
    try { const r = await ApiCall.post(`/piece-trace/items/${itemId}/labels`); setRows(r.data.data || []); setMsg({ severity: "success", text: r.data.message }); }
    catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not registered.") }); } finally { setBusy(false); }
  };
  const reprint = async (row) => {
    if (reason.trim().length < 4) { setMsg({ severity: "error", text: "Give the reason for the reprint (at least 4 characters)." }); return; }
    setBusy(true); setMsg(null);
    try {
      await ApiCall.post(`/labels/${row.lastLabelId}/reprint`, { reason: reason.trim() });
      const r = await ApiCall.get(`/piece-trace/items/${itemId}/labels`); setRows(r.data.data || []);
      setMsg({ severity: "success", text: `Reprint registered for ${row.mark} ${row.display}. Same piece identity, new label.` });
    } catch (e) { setMsg({ severity: "error", text: msgOf(e, "Not reprinted.") }); } finally { setBusy(false); }
  };

  const ready = printable(rows);
  const set = (k, v) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); if (k === "jobId") p.delete("itemId"); setParams(p); };

  return (
    <>
      <Helmet>
        <title>Piece labels | SFM</title>
        <style>{`
          @media print {
            @page { size: 3in 1in; margin: 0; }
            body * { visibility: hidden !important; }
            .sfm-label-sheet, .sfm-label-sheet * { visibility: visible !important; }
            .sfm-label-sheet { position: absolute; left: 0; top: 0; }
            .sfm-label { page-break-after: always; break-after: page; border: none !important; margin: 0 !important; }
          }
        `}</style>
      </Helmet>
      <Container maxWidth="lg">
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Piece labels</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          One 3 in x 1 in QR label per physical piece. Register the labels, then print. A voided label needs a reprint with a reason.
        </Typography>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField select size="small" label="Job" value={jobId} onChange={(e) => set("jobId", e.target.value)} sx={{ minWidth: 260 }}>
              <MenuItem value="">Choose a job</MenuItem>
              {jobs.map((j) => <MenuItem key={j.id} value={String(j.id)}>{j.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Mark" value={itemId} onChange={(e) => set("itemId", e.target.value)} sx={{ minWidth: 260 }} disabled={!jobId}>
              <MenuItem value="">Choose a mark</MenuItem>
              {marks.map((m) => <MenuItem key={m.id} value={String(m.id)}>{m.name} (qty {m.quantity})</MenuItem>)}
            </TextField>
            <Button variant="outlined" disabled={!itemId || busy} onClick={register}>Register labels</Button>
            <Button variant="contained" disabled={!ready.length} onClick={() => window.print()}>Print {ready.length} label{ready.length === 1 ? "" : "s"}</Button>
          </Stack>
          {msg && <Alert severity={msg.severity} sx={{ mt: 2 }}>{msg.text}</Alert>}
        </Paper>

        {rows.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <SmartTable tableId="piece-labels" lockedColumns={["Piece", "Identity"]} size="small">
              <TableHead><TableRow><TableCell>Piece</TableCell><TableCell>Identity</TableCell><TableCell>Label</TableCell></TableRow></TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.pieceId}>
                    <TableCell>{r.mark} {r.display}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{r.publicId}</TableCell>
                    <TableCell>
                      {r.labelUuid ? "Registered" : r.needsReprint ? (
                        canReprint ? <Button size="small" color="warning" disabled={busy} onClick={() => reprint(r)}>Reprint (voided)</Button>
                          : <Typography variant="caption" color="error">Voided: reprint needed</Typography>
                      ) : "Not registered"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </SmartTable>
            {rows.some((r) => r.needsReprint) && canReprint && (
              <TextField fullWidth size="small" sx={{ mt: 2 }} label="Reason for the reprint (recorded)" value={reason} onChange={(e) => setReason(e.target.value)} />
            )}
          </Paper>
        )}

        <Box className="sfm-label-sheet" sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {ready.map((r) => (
            <Box key={r.labelUuid} className="sfm-label"
              sx={{ width: "3in", height: "1in", boxSizing: "border-box", border: "1px dashed #999", p: "0.06in", display: "flex", gap: "0.08in",
                alignItems: "center", bgcolor: "#fff", color: "#000", overflow: "hidden" }}>
              <QRCodeSVG value={r.qr} size={84} level="M" includeMargin={false} style={{ width: "0.86in", height: "0.86in", flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, lineHeight: 1.1 }}>
                <Box sx={{ fontWeight: 800, fontSize: "13pt", whiteSpace: "nowrap" }}>{r.mark}</Box>
                <Box sx={{ fontWeight: 800, fontSize: "12pt" }}>{r.display}</Box>
                <Box sx={{ fontFamily: "monospace", fontSize: "6.5pt", wordBreak: "break-all" }}>{r.publicId}</Box>
                <Box sx={{ fontSize: "6.5pt", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.jobName}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </>
  );
}
